import { trace } from 'owa-trace';

/**
 * SortedKeyMap is a data structure which can help in retrieving values from a map in a range of keys
 */
export class SortedKeyMap<T, K> {
    private objectMap = new Map();
    private sortedKeysArray = [];

    constructor(
        private comparator: (key1: T, key2: T) => -1 | 0 | 1,
        private keySerializer: (key: T) => any
    ) {}

    /**
     * Returns size of the array of keys
     */
    get size() {
        return this.sortedKeysArray.length;
    }

    /**
     * Returns value for a given key
     * @param key Key
     */
    get(key: T) {
        return this.objectMap.get(this.keySerializer(key));
    }

    /**
     * Sets a value for a given key
     * @param key Key to pass
     * @param value Value to set
     */
    set(key: T, value: K) {
        const serializedKey = this.keySerializer(key);
        if (!this.objectMap.get(serializedKey)) {
            const indexToInsert = this.binarySearch(key);
            this.sortedKeysArray.splice(indexToInsert, 0, key);
        } else {
            trace.warn('[SortedKeyMap] Key already added so not putting in array.');
        }

        if (!value) {
            trace.warn('[SortedKeyMap] Empty value being set in SortedKeyMap');
        }
        this.objectMap.set(serializedKey, value);
    }

    binarySearch(key: T) {
        let left = 0;
        let right = this.sortedKeysArray.length - 1;

        while (left <= right) {
            const midPos = Math.floor((left + right) / 2);
            const compareValue = this.comparator(this.sortedKeysArray[midPos], key);

            // look in left or right half accordingly
            if (compareValue === -1) {
                left = midPos + 1;
            } else if (compareValue === 1) {
                right = midPos - 1;
            } else {
                return midPos;
            }
        }
        // In ideal binarySearch, it should return -1 but then here this logic is also being
        // used for finding the right position for insertion, so -1 does not make sense.
        // And in other places, call is made with the assumption that item is present.
        // This can though be improved to possibly pass the mode of search and return -1 if it is called with the intent
        // to find an element.
        return left;
    }

    /**
     * This method returns an array of sub arrays where a sub array contains key and its value
     * @param key1 Begin Range Key
     * @param key2 End Range key
     * @param includeEnd Whether to include the end range key in the return data
     */
    getRange(key1: T, key2: T, includeEnd?: boolean, onlyValues?: boolean) {
        const resultToReturn = [];
        if (!this.sortedKeysArray.length) {
            return resultToReturn;
        }
        const startIndex = this.binarySearch(key1);
        const endIndex = this.binarySearch(key2);
        const endBoundToCheck = includeEnd ? endIndex : endIndex - 1;

        let resultFormatter = null;
        if (onlyValues) {
            resultFormatter = (keyToUse: T) => this.get(keyToUse);
        } else {
            resultFormatter = (keyToUse: T) => [keyToUse, this.get(keyToUse)];
        }

        for (let index = startIndex; index <= endBoundToCheck; index++) {
            const keyToUse = this.sortedKeysArray[index];
            if (keyToUse) {
                resultToReturn.push(resultFormatter(keyToUse));
            }
        }
        return resultToReturn;
    }

    /**
     * This is used to delete a key
     * @param key Key to delete
     */
    delete(key: T) {
        if (!key) {
            trace.warn('[SortedKeyMap] Key used for deletion is empty');
            return;
        }
        const result = this.objectMap.delete(this.keySerializer(key));
        if (result) {
            // Delete in array only when item in map deleted
            const foundIndexInBinarySearch = this.binarySearch(key);
            if (foundIndexInBinarySearch === -1) {
                trace.warn('[SortedKeyMap] Element not found in array');
            } else {
                this.sortedKeysArray.splice(foundIndexInBinarySearch, 1);
            }
        }
    }

    /**
     * This clears the map
     */
    clear() {
        this.objectMap.clear();
        this.sortedKeysArray = [];
    }
}
